import obelisk from '../../../clients/obelisk';

/**
 * Get all KYC submissions pending review
 * GET /api/v1/Associate/GetKYCPendingReview
 * @returns {Promise<Array>}
 */
export const getPendingKYCReviews = async () => {
  try {
    const response = await obelisk().get('getKYCPendingReview');
    return response?.data || [];
  } catch (error) {
    console.error('Error fetching pending KYC reviews:', error);
    throw error;
  }
};

/**
 * Get all KYC submissions with filtering & pagination
 * POST /api/v1/Associate/GetAllKYC
 * @param {object} filters
 * @param {string} [filters.status] - Filter by status
 * @param {string} [filters.countryCode] - Filter by country
 * @param {string} [filters.type] - Filter by type (W9, W8BEN)
 * @param {string} [filters.reviewerName] - Filter by reviewer
 * @param {string} [filters.sortBy] - Field to sort by
 * @param {string} [filters.sortOrder] - 'asc' or 'desc'
 * @param {number} [filters.page] - Page number (1-based)
 * @param {number} [filters.pageSize] - Items per page
 * @returns {Promise<Object>}
 */
export const getAllKYC = async (filters = {}) => {
  try {
    const payload = {
      status: filters.status || '',
      countryCode: filters.countryCode || '',
      type: filters.type || '',
      reviewerName: filters.reviewerName || '',
      sortBy: filters.sortBy || '',
      sortOrder: filters.sortOrder || '',
      page: filters.page || 1,
      pageSize: filters.pageSize || 20,
    };
    console.log('[kycAdminService] getAllKYC payload:', payload);
    const response = await obelisk().post('getAllKYC', payload);
    console.log('[kycAdminService] getAllKYC response:', response?.data);
    return response?.data;
  } catch (error) {
    console.error('Error fetching all KYC records:', error);
    throw error;
  }
};

/**
 * Get KYC for a specific associate
 * GET /api/v1/Associate/GetKYC?associateId=xxx
 * @param {number} associateId
 * @returns {Promise<Object|null>}
 */
export const getKYCByAssociateId = async (associateId) => {
  try {
    const response = await obelisk().get('getKYC', { associateId });
    const data = response?.data || [];
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('Error fetching KYC:', error);
    throw error;
  }
};

/**
 * Approve a KYC submission
 * POST /api/v1/Associate/ApproveKYC?associateId=xxx
 * @param {number} associateId
 * @returns {Promise<Object>}
 */
export const approveKYC = async (associateId) => {
  try {
    console.log('[kycAdminService] approveKYC:', { associateId });
    const client = obelisk();
    const url = `${client.getCallByType('approveKYC')}?associateId=${associateId}`;
    const response = await client.http.post(url, {}, { headers: client.headers });
    console.log('[kycAdminService] approveKYC response:', response?.data);
    return response?.data;
  } catch (error) {
    console.error('Error approving KYC:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Reject a KYC submission
 * POST /api/v1/Associate/RejectKYC (JSON body)
 * @param {number} associateId
 * @param {string} internalNotes
 * @param {string} associateNotes
 * @returns {Promise<Object>}
 */
export const rejectKYC = async (associateId, internalNotes = '', associateNotes = '') => {
  try {
    const payload = {
      associateId,
      internalNotes,
      associateNotes,
    };
    console.log('[kycAdminService] rejectKYC payload:', payload);
    const response = await obelisk().post('rejectKYC', payload);
    console.log('[kycAdminService] rejectKYC response:', response?.data);
    return response?.data;
  } catch (error) {
    console.error('Error rejecting KYC:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Request more information from an associate (NEEDS MORE INFO)
 *
 * The dedicated RequestMoreInfoKYC endpoint does NOT exist (returns 404).
 * Strategy: Use UpdateKYC to set status + notes + preserve reviewer.
 * After update, re-fetch to verify the changes stuck.
 *
 * @param {number} associateId
 * @param {string} internalNotes
 * @param {string} associateNotes
 * @param {object} kycData - Full KYC object (needed for reviewer preservation)
 * @returns {Promise<Object>}
 */
export const requestMoreInfoKYC = async (
  associateId,
  internalNotes = '',
  associateNotes = '',
  kycData = null
) => {
  if (!kycData) {
    throw new Error('kycData is required to request more info');
  }

  const savedReviewer = kycData.reviewer || kycData.reviewerName || '';

  console.log('[kycAdminService] requestMoreInfoKYC — using UpdateKYC (RequestMoreInfoKYC endpoint is 404)');
  console.log('[kycAdminService] KYC data reviewer fields:', {
    reviewer: kycData.reviewer,
    reviewerName: kycData.reviewerName,
    resolved: savedReviewer,
  });

  // Step 1: Call UpdateKYC with NEEDS MORE INFO status
  const result = await updateKYCAdmin(kycData, {
    status: 'NEEDS MORE INFO',
    internalNotes,
    associateNotes,
  });

  // Step 2: Verify the update by re-fetching
  try {
    const freshKyc = await getKYCByAssociateId(associateId);
    console.log('[kycAdminService] Post-update verification:', {
      status: freshKyc?.status,
      reviewer: freshKyc?.reviewer,
      reviewerName: freshKyc?.reviewerName,
      internalNotes: freshKyc?.internalNotes?.substring(0, 50),
      associateNotes: freshKyc?.associateNotes?.substring(0, 50),
    });

    if (freshKyc?.status && !freshKyc.status.toLowerCase().includes('need') &&
        !freshKyc.status.toLowerCase().includes('info')) {
      console.warn('[kycAdminService] WARNING: Status was not set to NEEDS MORE INFO!',
        'Backend returned:', freshKyc.status);
    }
  } catch (verifyErr) {
    console.warn('[kycAdminService] Verification re-fetch failed (non-fatal):', verifyErr);
  }

  return result;
};

/**
 * Assign a KYC submission to the current reviewer
 * POST /api/v1/Associate/AssignKYC (JSON body)
 * @param {number} associateId
 * @returns {Promise<Object>}
 */
export const assignKYC = async (associateId) => {
  try {
    const payload = { associateId };
    console.log('[kycAdminService] assignKYC payload:', payload);
    const response = await obelisk().post('assignKYC', payload);
    console.log('[kycAdminService] assignKYC response:', response?.data);
    return response?.data;
  } catch (error) {
    console.error('Error assigning KYC:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

/**
 * Update KYC record (for general updates, NOT for status changes)
 * @param {Object} kycData - Full KYC data object
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>}
 */
export const updateKYCAdmin = async (kycData, updates) => {
  try {
    // The API returns reviewer as "reviewerName" but may accept "reviewer" or both
    const resolvedReviewer =
      updates.reviewer ?? updates.reviewerName ??
      kycData.reviewer ?? kycData.reviewerName ?? '';

    console.log('[kycAdminService] updateKYCAdmin called with:', {
      kycId: kycData.kycId,
      associateId: kycData.associateId,
      resolvedReviewer,
      updates,
    });

    const payload = {
      kycId: kycData.kycId,
      associateId: kycData.associateId,
      countryCode: updates.countryCode ?? kycData.countryCode ?? '',
      submittedDate: updates.submittedDate ?? kycData.submittedDate,
      apporvedDate: updates.apporvedDate ?? updates.approvedDate ?? kycData.apporvedDate ?? kycData.approvedDate,
      reviewer: resolvedReviewer,
      reviewerName: resolvedReviewer,
      internalNotes: updates.internalNotes ?? kycData.internalNotes ?? '',
      associateNotes: updates.associateNotes ?? kycData.associateNotes ?? '',
      selfieImage: updates.selfieImage ?? kycData.selfieImage ?? '',
      frontIdImage: updates.frontIdImage ?? kycData.frontIdImage ?? '',
      backIdImage: updates.backIdImage ?? kycData.backIdImage ?? '',
      selfieWithIdImage: updates.selfieWithIdImage ?? kycData.selfieWithIdImage ?? '',
      taxDoc: updates.taxDoc ?? kycData.taxDoc ?? '',
      proffOfAddressImage:
        updates.proffOfAddressImage ?? kycData.proffOfAddressImage ?? '',
      type: updates.type ?? kycData.type ?? '',
      status: updates.status ?? kycData.status ?? 'STARTED',
    };

    console.log('[kycAdminService] updateKYCAdmin payload:', JSON.stringify(payload, null, 2));
    const response = await obelisk().post('updateKYC', payload);
    console.log('[kycAdminService] updateKYCAdmin response:', response?.data);
    return response?.data;
  } catch (error) {
    console.error('[kycAdminService] updateKYCAdmin FAILED:', error);
    console.error('[kycAdminService] Error response:', error?.response?.data);
    console.error('[kycAdminService] Error status:', error?.response?.status);
    throw error;
  }
};

/**
 * Get associate profile information
 * @param {number} associateId
 * @returns {Promise<Object>}
 */
export const getAssociateProfile = async (associateId) => {
  try {
    const response = await obelisk().get('getAssociate', { associateId });
    return response?.data;
  } catch (error) {
    console.error('Error fetching associate profile:', error);
    throw error;
  }
};
